import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import CodeEditor from '../CodeEditor';
import { customFormsAPI, projectsAPI } from '../../services/api';

const CustomFormsTab = ({ projectId, projectSlug }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [fields, setFields] = useState([]);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  
  // Form state
  const [customForm, setCustomForm] = useState({
    htmlContent: '',
    cssContent: '',
    jsContent: '',
    isActive: false,
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const messageTimeoutRef = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    messageTimeoutRef('success', `Copiado: ${text}`);
  };

  const loadFields = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      if (response.data.success && response.data.project.fields) {
        setFields(response.data.project.fields);
      }
    } catch (error) {
      console.error('Error loading fields:', error);
    }
  };

  useEffect(() => {
    loadCustomForm();
    loadFields();
  }, [projectId]);

  const loadCustomForm = async () => {
    setLoading(true);
    try {
      const response = await customFormsAPI.get(projectId);
      if (response.data.success && response.data.data) {
        setCustomForm({
          htmlContent: response.data.data.html_content || '',
          cssContent: response.data.data.css_content || '',
          jsContent: response.data.data.js_content || '',
          isActive: response.data.data.is_active || false,
        });
      }
    } catch (error) {
      console.error('Error loading custom form:', error);
      // Si no existe, simplemente lasciamos los valores por defecto
      if (error.response?.status === 404) {
        setCustomForm({
          htmlContent: '',
          cssContent: '',
          jsContent: '',
          isActive: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (field, value) => {
    setCustomForm(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        htmlContent: customForm.htmlContent,
        cssContent: customForm.cssContent,
        jsContent: customForm.jsContent,
        isActive: customForm.isActive,
      };
      
      await customFormsAPI.update(projectId, data);
      setHasChanges(false);
      messageTimeoutRef('success', 'Configuración guardada exitosamente');
      await loadCustomForm();
    } catch (error) {
      console.error('Error saving custom form:', error);
      const errorMsg = error.response?.data?.error || 'Error al guardar la configuración';
      messageTimeoutRef('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar toda la configuración del formulario personalizado?')) {
      return;
    }

    setSaving(true);
    try {
      await customFormsAPI.delete(projectId);
      setCustomForm({
        htmlContent: '',
        cssContent: '',
        jsContent: '',
        isActive: false,
      });
      setHasChanges(false);
      messageTimeoutRef('success', 'Configuración eliminada');
    } catch (error) {
      console.error('Error deleting custom form:', error);
      messageTimeoutRef('error', 'Error al eliminar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open(`/form/${projectSlug}`, '_blank');
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold">Formulario Personalizado</h2>
          <p className="text-sm text-gray-500 mt-1">
            Personaliza el formulario de registro con código HTML, CSS y JavaScript
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={customForm.isActive}
              onChange={(e) => {
                setCustomForm(prev => ({ ...prev, isActive: e.target.checked }));
                setHasChanges(true);
              }}
              className="mr-2 h-4 w-4 text-primary-600"
            />
            <span className="text-sm font-medium">Activar</span>
          </label>
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
          >
            Vista Previa
          </button>
        </div>
      </div>

      {/* Warning Banner - Ley 29733 */}
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Cumplimiento con Ley 29733 - Protección de Datos Personales
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                El formulario personalizado se ejecuta en un iframe aislado por seguridad. 
                Asegúrate de no incluir campos que collecten datos biométricos sin el 
                consentimiento explícito requerido por la Ley 29733.
              </p>
            </div>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`mb-4 p-4 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Placeholder Reference */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <button
          type="button"
          onClick={() => setShowPlaceholders(!showPlaceholders)}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-sm font-semibold">Placeholders Disponibles</h3>
          <svg
            className={`w-5 h-5 transition-transform ${showPlaceholders ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showPlaceholders && (
          <div className="mt-4 space-y-4">
            {/* Block Placeholders */}
            <div>
              <h4 className="text-xs font-medium text-gray-600 mb-2">Bloques</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['{FIELDS}', '{FACIAL}', '{LEGAL}', '{SUBMIT}'].map((placeholder) => (
                  <div key={placeholder} className="flex items-center group">
                    <code className="bg-gray-200 px-2 py-1 rounded text-xs flex-1 cursor-pointer hover:bg-gray-300 transition"
                      onClick={() => copyToClipboard(placeholder)}
                      title="Click para copiar"
                    >
                      {placeholder}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(placeholder)}
                      className="ml-1 p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition"
                      title="Copiar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-Field Placeholders */}
            {fields.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-600 mb-2">Por Campo (Input)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {fields.map((field) => (
                    <div key={`input-${field.id}`} className="flex items-center group">
                      <code className="bg-blue-100 px-2 py-1 rounded text-xs flex-1 cursor-pointer hover:bg-blue-200 transition"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_input}`)}
                        title="Click para copiar"
                      >
                        {`{field_${field.field_name}_input}`}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_input}`)}
                        className="ml-1 p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition"
                        title="Copiar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-Field Placeholders - Label */}
            {fields.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-600 mb-2">Por Campo (Label)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {fields.map((field) => (
                    <div key={`label-${field.id}`} className="flex items-center group">
                      <code className="bg-green-100 px-2 py-1 rounded text-xs flex-1 cursor-pointer hover:bg-green-200 transition"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_label}`)}
                        title="Click para copiar"
                      >
                        {`{field_${field.field_name}_label}`}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_label}`)}
                        className="ml-1 p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition"
                        title="Copiar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-Field Placeholders - Name */}
            {fields.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-600 mb-2">Por Campo (Nombre)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {fields.map((field) => (
                    <div key={`name-${field.id}`} className="flex items-center group">
                      <code className="bg-purple-100 px-2 py-1 rounded text-xs flex-1 cursor-pointer hover:bg-purple-200 transition"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_name}`)}
                        title="Click para copiar"
                      >
                        {`{field_${field.field_name}_name}`}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(`{field_${field.field_name}_name}`)}
                        className="ml-1 p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition"
                        title="Copiar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Individual Field Placeholders */}
            {fields.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-600 mb-2">Individuales (Completo)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {fields.map((field) => (
                    <div key={`full-${field.id}`} className="flex items-center group">
                      <code className="bg-orange-100 px-2 py-1 rounded text-xs flex-1 cursor-pointer hover:bg-orange-200 transition"
                        onClick={() => copyToClipboard(`{field_${field.field_name}}`)}
                        title="Click para copiar"
                      >
                        {`{field_${field.field_name}}`}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(`{field_${field.field_name}}`)}
                        className="ml-1 p-1 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition"
                        title="Copiar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-3">
          Haz click en cualquier placeholder para copiarlo. Usa estos placeholders en tu HTML personalizado.
        </p>
      </div>

      <div className="space-y-6">
        {/* HTML Editor */}
        <CodeEditor
          language="html"
          value={customForm.htmlContent}
          onChange={(value) => handleEditorChange('htmlContent', value)}
        />

        {/* CSS Editor */}
        <CodeEditor
          language="css"
          value={customForm.cssContent}
          onChange={(value) => handleEditorChange('cssContent', value)}
        />

        {/* JavaScript Editor */}
        <CodeEditor
          language="javascript"
          value={customForm.jsContent}
          onChange={(value) => handleEditorChange('jsContent', value)}
        />
      </div>

      <div className="flex justify-between items-center mt-6 pt-6 border-t">
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving || (!customForm.htmlContent && !customForm.cssContent && !customForm.jsContent)}
          className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Eliminar Configuración
        </button>
        
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

CustomFormsTab.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  projectSlug: PropTypes.string.isRequired,
};

export default CustomFormsTab;
